import { Button, Container, Input } from "reactstrap";
import API from "../../../../api/api/API";
import FileDnd from "../../../../components/FormBuilder/CustomizationTab/BuildTab/components/FileDnd";
import Cookies from "js-cookie";
import AppPageHeader from "../../../../components/Headers/AppPageHeader";
import { Fragment, useState } from "react";
import { newToastEvent } from "../../../../utils/ToastUtil";
import ItemCardList from "../../../../components/Modules/ItemCardList";
import SummaryCard from "../../../../components/Modules/SummaryCard";
import EditFieldDrawer from "../../../../components/Drawers/Fields/EditFieldDrawer";
import AppSpinner from "../../../../elements/AppSpinner";
import StrUtils from "../../../../utils/StrUtils";
import CreateFieldDrawer from "../../../../components/Drawers/Fields/CreateFieldDrawer";
import TenantAPI from "../../../../api/api/TenantAPI";

const availableFieldTypes: Record<string, string[]> = {
  Text: ["Text", "LongText", "RichText", "SingleSelect"],
  LongText: ["Text", "LongText", "RichText", "SingleSelect"],
  RichText: ["Text", "LongText", "RichText"],
  SingleSelect: ["Text", "LongText", "RichText", "SingleSelect"],
  Number: ["Number", "Percent", "Currency", "CurrencyInterval"],
  Percent: ["Number", "Percent", "Currency", "CurrencyInterval"],
  Currency: ["Number", "Percent", "Currency", "CurrencyInterval"],
  CurrencyInterval: ["Number", "Percent", "Currency", "CurrencyInterval"],
  Date: ["Date", "DateTime", "Time"],
  DateTime: ["Date", "DateTime", "Time"],
  Time: ["Date", "DateTime", "Time"],
  Toggle: ["Toggle", "Text"],
  Image: ["Image", "SingleFile"],
  SingleFile: ["SingleFile"],
  Audio: ["Audio", "SingleFile"],
  Video: ["Video", "SingleFile"],
  URL: ["URL", "Text", "LongText", "RichText"],
  Email: ["Email", "Text", "LongText", "RichText"],
  Phone: ["Phone", "Text", "LongText", "RichText"],
  SingleReference: ["SingleReference", "MultiReference"],
  MultiReference: ["MultiReference"],
  MultiSelect: ["MultiSelect", "Text", "LongText", "RichText"],
  Tags: ["Tags"],
  Subitem: ["Subitem", "MultiSubitem", "Object"],
  Object: ["Object", "Subitem", "MultiSubitem"],
  MultiSubitem: ["MultiSubitem", "Subitem", "Object"],
  Location: ["Location", "Text", "LongText"],
  Filter: ["Filter", "Object"],
  Calculated: ["Calculated"],
};

export default function JSONToSchema() {
  const [processing, setProcessing] = useState(false);
  const [fields, setFields] = useState<any[] | null>(null);
  const [editField, setEditField] = useState<any>(null);
  const [moduleName, setModuleName] = useState("");
  const [createField, setCreateField] = useState(false);

  return (
    <Container fluid>
      <div className="header pb-2">
        <AppPageHeader
          title="JSON to Schema"
          description="Convert JSON to a GrowthCloud Schema"
          customContent={
            fields ? (
              <>
                <Button
                  onClick={() => {
                    setFields(null);
                    setEditField(null);
                    setModuleName("");
                  }}
                >
                  Restart
                </Button>
                <Button
                  color="primary"
                  onClick={downloadSchema}
                  disabled={!moduleName}
                >
                  Download
                </Button>
              </>
            ) : undefined
          }
        />
      </div>
      {!processing && !fields && (
        <FileDnd
          upload={sendJSON}
          uploading={false}
          accept="application/json"
        />
      )}
      {processing && (
        <>
          <div className="tw:flex tw:justify-center tw:items-center tw:h-full tw:w-full tw:mb-3">
            <AppSpinner size="md" />
          </div>
          <div className="tw:flex tw:justify-center tw:items-center tw:h-full tw:w-full gap-2">
            <div className="tw:text-dark">Processing...</div>
          </div>
        </>
      )}
      {fields && (
        <div className="tw:mb-8">
          <h4>Module Name</h4>
          <Input
            bsSize="sm"
            className="tw:mb-6"
            placeholder="Module Name"
            value={moduleName}
            onChange={(e) => setModuleName(e.target.value)}
          />

          <AppPageHeader
            className="tw:mb-4"
            size="sm"
            title={"Custom Fields"}
            description={`Create and manage the custom fields for this data source.`}
            customContent={
              <Button
                size="sm"
                color="primary"
                onClick={() => setCreateField(true)}
                style={{ width: 80 }}
              >
                Add
              </Button>
            }
          />
          {fields?.map((f, i) => (
            <Fragment key={f.APIName}>
              <ItemCardList
                removable
                removeAction={() =>
                  setFields((prev) =>
                    prev
                      ? prev.filter((item) => item.APIName !== f.APIName)
                      : prev
                  )
                }
                removeStatus={"Delete Field"}
                editable
                editableAction={() => setEditField(structuredClone(f))}
                summary
                item={f}
              >
                <SummaryCard field={f} />
              </ItemCardList>
              {fields?.length - 1 !== i && <hr className="my-0"></hr>}
            </Fragment>
          ))}
        </div>
      )}
      <CreateFieldDrawer
        open={createField}
        onClose={() => setCreateField(false)}
        onSave={(newField: any) => {
          setFields((prev) => {
            if (!prev) return prev;

            return [...prev, TenantAPI.getFieldSchemaFromFieldValues(newField)];
          });
          setCreateField(false);
        }}
      />
      <EditFieldDrawer
        onClose={() => setEditField(null)}
        onSave={(_: any, updatedField: any) => {
          setFields((prev) => {
            if (!prev) return prev;

            return prev.map((field) => {
              if (field.APIName === editField.APIName) {
                return TenantAPI.getFieldSchemaFromFieldValues(updatedField);
              }

              return field;
            });
          });

          setEditField(null);
        }}
        open={!!editField}
        submitting={false}
        error={null}
        field={editField} // If provided drawer knows we are editing a subitem field.
        allowFieldTypeEdit
        availableFieldTypes={
          availableFieldTypes?.[editField?.FieldType as string]
        }
        moduleAPIName={""}
      />
    </Container>
  );

  async function sendJSON(e: any) {
    const file = e?.target?.files?.[0];

    const formData = new FormData();
    formData.append("file", file);

    try {
      setProcessing(true);

      const res = await API._bearerRequest(
        "POST",
        "/mc-tools/json-to-schema",
        formData,
        Cookies.get(`authenticationToken`)
      );

      if (!res?.data?.data?.fields) {
        return newToastEvent("error", "Failed to convert JSON");
      }

      setFields(res?.data?.data?.fields);
    } catch {
      newToastEvent("error", "Failed to convert JSON");
    } finally {
      setProcessing(false);
    }
  }

  function downloadSchema() {
    if (!fields || !moduleName) {
      return newToastEvent("error", "Missing required information!");
    }

    const apiName = StrUtils.cleanAPIName(moduleName);

    const moduleSchema = {
      Name: moduleName,
      APIName: apiName,
      Fields: fields.map((f) => {
        if (
          ["Text", "LongText", "RichText"].includes(f?.FieldType) &&
          f?.SourcesList
        ) {
          delete f.SourcesList;
        }
        return f;
      }),
      Templates: {},
    };

    const blob = new Blob([JSON.stringify(moduleSchema)], {
      type: "application/json",
    });

    const url = window.URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.id = "module-schema-download-link";
    a.href = url;
    a.download = `${apiName}.json`;
    a.click();

    window.URL.revokeObjectURL(url);
    document.getElementById("module-schema-download-link")?.remove();
  }
}
